// MerchantOrdersScreen/performance.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Button, Collapse, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { useMerchant } from "../../context/merchant";
import { useModalManager } from "../../hooks/useModalManager";
import RatingDetailModal from "../../modals/rating-detail";
import WeeklyBestProductsModal from "../../modals/weekly-best-products";
import { getMerchantPerformance } from "../../api/axios/requests";
import "../../css/orders-insight.css";

const EMPTY_WEEK = {
  rating: 0, totalOrders: 0, lateOrders: 0, overallPercent: 0, rank: 1,
  cancelledOrders: 0, cancelPenalty: 0,
  dailyBreakdown: Array.from({ length: 7 }, () => ({ total: 0, bad: 0, rating: 0, reviews: 0 })),
};

const DEFAULT_STATS = {
  currentRating: 0,
  categoryTotal: 0,
  categoryName: "Ангилал",
  currentRank: 1,
  promoRank: 1,
  weeklyTier: 0,
  weeklyScore: 0,
  isWeeklyBest: false,
  current: EMPTY_WEEK,
  last: EMPTY_WEEK,
  passedDays: 1,
  dailyCurrent: EMPTY_WEEK.dailyBreakdown,
  dailyLast: EMPTY_WEEK.dailyBreakdown,
};

const onTimePercent = ({ totalOrders, lateOrders }) =>
  totalOrders ? Math.round(((totalOrders - lateOrders) / totalOrders) * 100) : 0;

const ratingPercent = (rating) => Math.round((rating / 5) * 100);

const TIERS = [
  { label: "Стандарт цол",  emoji: null,  color: "#9e9e9e", bg: "#f5f5f5" },
  { label: "Мөнгөн цол",   emoji: "🥈",  color: "#607d8b", bg: "#eceff1" },
  { label: "Алтан цол",    emoji: "🥇",  color: "#f9a825", bg: "#fffde7" },
  { label: "Платинум цол", emoji: "💎",  color: "#0288d1", bg: "#e1f5fe" },
];

function TierBadge({ tier, score }) {
  const t = TIERS[Math.min(tier, 3)];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 14px", borderRadius: 20,
      backgroundColor: t.bg, border: `1.5px solid ${t.color}20`,
    }}>
      {t.emoji && <span style={{ fontSize: 18 }}>{t.emoji}</span>}
      <span style={{ fontWeight: 700, color: t.color, fontSize: 15 }}>{t.label}</span>
      {score > 0 && (
        <span style={{ fontSize: 12, color: t.color, opacity: 0.8 }}>· {score}%</span>
      )}
    </div>
  );
}

/* Дугуй график — SVG, нэмэлт сан шаардахгүй */
function Donut({ percent, children }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.min(Math.max(percent, 0), 100) / 100) * circumference;

  return (
    <div className="donut">
      <svg viewBox="0 0 100 100">
        <circle
          className="donut__track"
          cx="50"
          cy="50"
          r={radius}
          fill="none"
        />
        <circle
          className="donut__value"
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeDasharray={`${filled} ${circumference}`}
          transform="rotate(-90 50 50)"
        />
      </svg>

      <div className="donut__center">{children}</div>
    </div>
  );
}

function WeekBlock({ title, range, data, rankDiff, onDetail }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="week-block">
      <button
        type="button"
        className="week-block__head"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className="week-block__title">
          {title} ({range})
        </span>

        <span className="week-block__stats">
          <span className="week-block__stat">
            <span className="week-block__stat-label">Нийт үнэлгээ</span>
            <span className="week-block__stat-value">
              {data.overallPercent}%
            </span>
          </span>

          <span className="week-block__stat">
            <span className="week-block__stat-label">Эрэмбэ</span>
            <span className="week-block__stat-value week-block__stat-value--rank">
              {data.rank > 0 ? data.rank : "–"}

              {!!rankDiff && (
                <span
                  className={`week-block__delta ${
                    rankDiff > 0 ? "is-up" : "is-down"
                  }`}
                >
                  <i
                    className={`mdi ${
                      rankDiff > 0 ? "mdi-arrow-up" : "mdi-arrow-down"
                    }`}
                  />
                  {rankDiff > 0 ? "+" : "-"}
                  {Math.abs(rankDiff)} байр
                </span>
              )}
            </span>
          </span>
        </span>

        <i
          className={`mdi mdi-chevron-down week-block__chevron ${
            open ? "is-open" : ""
          }`}
        />
      </button>

      <Collapse in={open}>
        <div>
          <div className="week-block__body">
            <div className="row g-3 align-items-stretch">
              <div className="col-12 col-md-6 d-flex">
                <div className="donut-col">
                  <div className="donut-col__label">Хэрэглэгчийн үнэлгээ</div>

                  <Donut percent={ratingPercent(data.rating)}>
                    <span className="donut__rating">
                      {data.rating.toFixed(1)}
                      <i className="mdi mdi-star" />
                    </span>
                  </Donut>

                  <Button
                    variant="primary"
                    className="rounded-pill donut-col__btn"
                    onClick={() => onDetail("rating")}
                  >
                    Дэлгэрэнгүй
                  </Button>
                </div>
              </div>

              <div className="col-12 col-md-6 d-flex">
                <div className="donut-col">
                  <div className="donut-col__label">
                    Захиалгыг 5 минут дотор БЭЛДЭЖ БАЙНА төлөвт шилжүүлдэг
                  </div>

                  <Donut percent={onTimePercent(data)}>
                    <span className="donut__orders">
                      <span className="donut__late">{data.lateOrders}</span>
                      <span className="donut__slash">/</span>
                      <span className="donut__total">{data.totalOrders}</span>
                    </span>
                  </Donut>

                  <Button
                    variant="primary"
                    className="rounded-pill donut-col__btn"
                    onClick={() => onDetail("speed")}
                  >
                    Дэлгэрэнгүй
                  </Button>
                </div>
              </div>
            </div>

            {(data.cancelledOrders ?? 0) > 0 && (
              <div className="mt-3 p-3" style={{
                background: "#fff3e0", borderRadius: 8, border: "1px solid #ffe0b2",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ color: "#e65100", fontWeight: 600 }}>
                  ⚠️ Цуцалсан захиалга: <b>{data.cancelledOrders}</b>
                </span>
                <span style={{ color: "#c62828", fontWeight: 700 }}>
                  -{data.cancelPenalty} оноо
                </span>
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default function MerchantPerformance() {
  const { name } = useMerchant();
  const { closeModal, currentModal, openModal } = useModalManager();
  const [selectedWeek, setSelectedWeek] = useState("current");
  const [selectedType, setSelectedType] = useState("speed");
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [showProductPicker, setShowProductPicker] = useState(false);

  useEffect(() => {
    let alive = true;
    getMerchantPerformance()
      .then((res) => {
        if (alive && res?.success && res.data) setStats(res.data);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const {
    currentRating,
    currentRank,
    promoRank,
    categoryTotal,
    categoryName,
    last,
    current,
    passedDays,
    dailyLast,
    dailyCurrent,
    weeklyTier = 0,
    weeklyScore = 0,
    isWeeklyBest = false,
  } = stats;

  const currentStart = dayjs().subtract(passedDays - 1, "day");
  const lastStart = currentStart.subtract(7, "day");

  const range = (from) =>
    `${from.format("MM/DD")} - ${from.add(6, "day").format("MM/DD")}`;

  const buildDays = (items, start) =>
    items.map((item, i) => {
      const date = start.add(i, "day");

      return {
        key: date.format("YYYY-MM-DD"),
        label: date.format("dddd"),
        shortLabel: date.format("ddd"),
        date: date.format("MM/DD"),
        ...item,
      };
    });

  const weeks = useMemo(
    () => ({
      current: {
        title: "Энэ 7 хоног",
        range: range(currentStart),
        days: buildDays(dailyCurrent, currentStart),
      },
      last: {
        title: "Өнгөрсөн 7 хоног",
        range: range(lastStart),
        days: buildDays(dailyLast, lastStart),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailyLast, dailyCurrent, passedDays]
  );

  const openDetail = (weekKey, metricType) => {
    setSelectedWeek(weekKey);
    setSelectedType(metricType);
    openModal("rating-detail");
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-body rating-block">
        <h3 className="rating-name">{name || "Байгууллага"}</h3>

        {/* Толгой үзүүлэлт */}
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="stat-card">
              <div className="stat-card__title">Одоогийн үнэлгээ</div>

              <div className="stat-card__body">
                <div className="stat-card__value">
                  {currentRating.toFixed(1)}
                  <i className="mdi mdi-star stat-card__star" />
                  <span className="stat-card__percent">
                    {ratingPercent(currentRating)}%
                  </span>
                </div>

                <p className="stat-card__note mb-0">
                  Өмнөх 7 хоногийн амжилтаар тооцогдоно
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="stat-card">
              <div className="stat-card__title">Одоогийн эрэмбэ</div>
              <i className="mdi mdi-trophy stat-card__icon" />

              <div className="stat-card__body">
                <div className="stat-card__value">
                  <span className="stat-card__rank">{currentRank}</span>
                  <span className="stat-card__total">/{categoryTotal}</span>
                </div>

                <div style={{ marginTop: 8, marginBottom: 6 }}>
                  <TierBadge tier={weeklyTier} score={weeklyScore} />
                </div>

                <p className="stat-card__note mb-0">
                  {categoryName} ангилалын {categoryTotal} газраас {currentRank}
                  -д эрэмбэлэгдэж байна
                </p>

                {weeklyTier < 3 && (
                  <p className="stat-card__note mb-0" style={{ marginTop: 4 }}>
                    {TIERS[weeklyTier + 1].emoji} <b>{TIERS[weeklyTier + 1].label}</b>{" "}
                    авахад <b>{
                      weeklyTier === 0 ? 55 :
                      weeklyTier === 1 ? 72 : 88
                    }%</b> хүрэх шаардлагатай
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rating-promo">
          <span className="rating-promo__emoji">
            {weeklyTier === 3 ? "💎" : weeklyTier === 2 ? "🥇" : weeklyTier === 1 ? "🥈" : "🏆"}
          </span>

          <p className="rating-promo__text mb-0">
            {weeklyTier === 3 ? (
              <>Та <b>Платинум цол</b>той байна! Хэрэглэгчийн апп дээр жагсаалтын эхэнд харагдаж байна 🎉</>
            ) : (
              <>
                Та одоогоор <b>{TIERS[weeklyTier].label}</b>той байна.
                {" "}<b>{TIERS[weeklyTier + 1]?.label}</b> авахад{" "}
                <b>{weeklyTier === 0 ? "55" : weeklyTier === 1 ? "72" : "88"}%</b>{" "}
                нийт оноо хүрэх шаардлагатай.
                {" "}Эхний <b>{promoRank}</b>-т эрэмбэлэгдсэнээр нэмэлт сурталчилгааны боломж нээгдэнэ 🚀
              </>
            )}
          </p>
        </div>

        <WeekBlock
          title="Энэ 7 хоногт"
          range={range(currentStart)}
          data={current}
          rankDiff={last.rank > 0 ? last.rank - current.rank : null}
          onDetail={(metricType) => openDetail("current", metricType)}
        />

        <WeekBlock
          title="Өнгөрсөн 7 хоногт"
          range={range(lastStart)}
          data={last}
          onDetail={(metricType) => openDetail("last", metricType)}
        />

        {/* Хэрэглэгчийн апп дээр шилдэг 3-т ҮНЭХЭЭР гарсан үед л харагдана.
            weeklyTier > 0 нь зөвхөн цолтой гэсэн үг тул шилдэг гэж үзэж болохгүй. */}
        {isWeeklyBest && (
          <div className="weekly-best-cta">
            <div className="weekly-best-cta__header">
              <span className="weekly-best-cta__trophy">🏆</span>
              <div>
                <div className="weekly-best-cta__title">Баяр хүргэе!</div>
                <div className="weekly-best-cta__subtitle">
                  Та энэ 7 хоногийн шилдэгээр тодорсон байна
                </div>
              </div>
            </div>
            <p className="weekly-best-cta__desc">
              Та <strong>E24 аппликейшны нүүр дэлгэц</strong> дээр гарах бараа бүтээгдэхүүнээ сонгон уу.
              Хэрвээ бараа сонгогдоогүй бол <strong>автоматаар хамгийн их борлуулалттай</strong> барааг тань гаргах болно.
            </p>
            <Button
              variant="warning"
              className="weekly-best-cta__btn"
              onClick={() => setShowProductPicker(true)}
            >
              <i className="mdi mdi-package-variant me-2" />
              Бараа сонгох
            </Button>
          </div>
        )}
      </div>

      <WeeklyBestProductsModal
        show={showProductPicker}
        handleClose={() => setShowProductPicker(false)}
        onSuccess={() => setShowProductPicker(false)}
      />

      <RatingDetailModal
        show={currentModal === "rating-detail"}
        handleClose={closeModal}
        week={weeks[selectedWeek]}
        type={selectedType}
      />
    </div>
  );
}
